import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft, Download, Trash2, Loader2, ShieldAlert,
  RefreshCw, FileText, Users, Building
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CleanupCenter = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/cleanup/preview');
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview (super-admin only)');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPreview(); }, []);

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${BACKEND}/api/admin/cleanup/preview.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('CSV download failed');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cleanup_preview_${Date.now()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error(e.message || 'CSV download failed');
    }
  };

  const execute = async () => {
    setExecuting(true);
    try {
      const res = await api.post('/admin/cleanup/execute?confirm=true');
      toast.success(
        `Archived ${res.data.users_archived} users + ${res.data.shifts_archived} shifts ` +
        `(${res.data.protected_users_skipped + res.data.protected_shifts_skipped} protected, skipped).`
      );
      setConfirmOpen(false);
      loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Execute failed');
    } finally {
      setExecuting(false);
    }
  };

  const counts = data?.counts || {};
  const totalCandidates = (counts.candidate_users || 0) + (counts.candidate_shifts || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-rose-50/30 to-white">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/admin/home')}
              data-testid="cleanup-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <ShieldAlert className="w-6 h-6 text-rose-500" /> Test Data Cleanup
              </h1>
              <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
                Dry-run preview of accounts and jobs whose emails/names/venues match
                <span className="font-mono mx-1 text-rose-600">{data?.pattern}</span>.
                Anything with a paid invoice or recorded payment is auto-protected.
                Nothing is deleted until you click <b>Execute Archive</b>.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={loadPreview} disabled={loading}
              data-testid="cleanup-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={downloadCsv} disabled={loading || !data}
              data-testid="cleanup-download-csv-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Download CSV
            </Button>
            <Button
              variant="destructive" size="sm"
              disabled={loading || totalCandidates === 0}
              onClick={() => setConfirmOpen(true)}
              data-testid="cleanup-execute-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Execute Archive
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
          </div>
        ) : !data ? (
          <Card><CardContent className="py-10 text-center text-sm text-gray-500">
            No preview available.
          </CardContent></Card>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryTile label="Users to Archive" value={counts.candidate_users} color="rose" Icon={Users}/>
              <SummaryTile label="Shifts to Archive" value={counts.candidate_shifts} color="rose" Icon={Building}/>
              <SummaryTile label="Protected Users" value={counts.protected_users} color="emerald" Icon={ShieldAlert}/>
              <SummaryTile label="Protected Shifts" value={counts.protected_shifts} color="emerald" Icon={ShieldAlert}/>
            </div>

            <Card data-testid="cleanup-candidate-users-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-rose-500"/> Candidate Users ({counts.candidate_users || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {(data.candidate_users || []).length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">No matching users. ✅</p>
                ) : (
                  <ListTable
                    columns={['Email', 'Role', 'Name', 'Created']}
                    rows={data.candidate_users.map(u => [u.email, u.role, u.name, formatDateMMDDYYYY(u.created_at)])}
                  />
                )}
              </CardContent>
            </Card>

            <Card data-testid="cleanup-candidate-shifts-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building className="w-4 h-4 text-rose-500"/> Candidate Shifts ({counts.candidate_shifts || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {(data.candidate_shifts || []).length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">No matching shifts. ✅</p>
                ) : (
                  <ListTable
                    columns={['Venue', 'City/State', 'Date', 'Status', 'Client']}
                    rows={data.candidate_shifts.map(s => [
                      s.venue_name, `${s.city||''}, ${s.state||''}`,
                      formatDateMMDDYYYY(s.shift_date), s.status, s.client_email
                    ])}
                  />
                )}
              </CardContent>
            </Card>

            {((data.protected_users||[]).length + (data.protected_shifts||[]).length) > 0 && (
              <Card className="border-emerald-200 bg-emerald-50/30" data-testid="cleanup-protected-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-emerald-700">
                    <ShieldAlert className="w-4 h-4"/> Protected (will NOT be archived)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {data.protected_users.map(u => (
                    <div key={`pu-${u.id}`} className="text-xs flex flex-wrap gap-2 items-center">
                      <Badge variant="outline" className="bg-white">user</Badge>
                      <span className="font-medium">{u.email}</span>
                      <span className="text-gray-500">({u.role})</span>
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">{u.reason}</Badge>
                    </div>
                  ))}
                  {data.protected_shifts.map(s => (
                    <div key={`ps-${s.id}`} className="text-xs flex flex-wrap gap-2 items-center">
                      <Badge variant="outline" className="bg-white">shift</Badge>
                      <span className="font-medium">{s.venue_name}</span>
                      <span className="text-gray-500">{formatDateMMDDYYYY(s.shift_date)}</span>
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">{s.reason}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <p className="text-[11px] text-gray-400 flex items-center gap-1">
              <FileText className="w-3 h-3"/> Preview generated {data.generated_at ? `${formatDateMMDDYYYY(data.generated_at)} ${String(data.generated_at).slice(11,19)}` : '—'} UTC · pattern: <code>{data.pattern}</code>
            </p>
          </>
        )}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="cleanup-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Archive {totalCandidates} record{totalCandidates === 1 ? '' : 's'}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will soft-delete (is_archived=true) <b>{counts.candidate_users} users</b> and
                <b> {counts.candidate_shifts} shifts</b>. Protected records will be skipped. You can
                restore any record later by unarchiving it from the User or Shift detail page.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={executing}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={execute}
                disabled={executing}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="cleanup-confirm-execute-btn"
              >
                {executing ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Trash2 className="w-4 h-4 mr-1"/>}
                Execute
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

const SummaryTile = ({ label, value, color, Icon }) => {
  const ring = color === 'emerald' ? 'border-emerald-200 bg-emerald-50/40' : 'border-rose-200 bg-rose-50/40';
  const txt  = color === 'emerald' ? 'text-emerald-700' : 'text-rose-700';
  return (
    <div className={`rounded-xl border ${ring} p-3`}>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Icon className={`w-3.5 h-3.5 ${txt}`}/> {label}
      </div>
      <div className={`text-2xl font-bold ${txt}`}>{value ?? 0}</div>
    </div>
  );
};

const ListTable = ({ columns, rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-gray-500 border-b">
          {columns.map(c => <th key={c} className="py-2 pr-3 font-medium">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b last:border-b-0">
            {r.map((cell, j) => (
              <td key={j} className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">{cell || '—'}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default CleanupCenter;
